import React from "react"
import PropTypes from "prop-types"
import { Box, Divider, Grid, Skeleton, Typography } from "@mui/material"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { ReactComponent as CheckIcon } from "src/v2/assets/icons/check-icon.svg"
import theme from "src/v2/theme/theme"

const SummaryItem = ({ label, value }) => {
    return (
        <Box display="flex" gap="5px" flexDirection="column">
            <Typography
                variant="contraHeading"
                sx={{ color: theme.palette.primary.white70 }}
            >
                {label}
            </Typography>

            <Typography
                variant="textBody"
                color="common.white"
                sx={{ whiteSpace: "pre-line" }}
            >
                {value ?? "-"}
            </Typography>
        </Box>
    )
}

SummaryItem.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.node,
    ]),
}

const EmptySummaryItem = ({ text }) => {
    return (
        <Typography variant="textBody" color="common.white">
            {text}
        </Typography>
    )
}

EmptySummaryItem.propTypes = {
    text: PropTypes.string.isRequired,
}

const SummarySkeletonItem = () => {
    return (
        <Box display="flex" gap="7px" flexDirection="column">
            <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                    width: "70%",
                    height: 12,
                    borderRadius: "2px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    "&::after": {
                        background:
                            "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0) 100%)",
                    },
                }}
            />

            <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                    width: "90%",
                    height: 18,
                    borderRadius: "2px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    "&::after": {
                        background:
                            "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0) 100%)",
                    },
                }}
            />
        </Box>
    )
}

const SummarySkeletonGrid = ({ fields }) => {
    const skeletonItems = fields?.length ? fields : Array.from({ length: 3 })

    return (
        <Grid container spacing={2}>
            {skeletonItems.map((field, index) => (
                <Grid
                    key={field?.label ?? index}
                    item
                    {...(field?.gridProps ?? {
                        xxxs: 4,
                        sm: 4,
                        md: 4,
                    })}
                    sx={{
                        minWidth: 120,
                    }}
                >
                    <SummarySkeletonItem />
                </Grid>
            ))}
        </Grid>
    )
}

SummarySkeletonGrid.propTypes = {
    fields: PropTypes.array,
}

const SummarySection = ({
    title,
    fields,
    empty,
    emptyText,
    onClick,
    fieldGridProps,
    loading,
}) => {
    const canClick = !loading && Boolean(onClick)

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                height: "100%",
                width: "100%",
            }}
        >
            <Divider
                orientation="vertical"
                flexItem
                sx={{
                    width: 2,
                    mr: "22px",
                    py: 3,
                    backgroundColor: theme.palette.common.white,
                    border: "none",
                    "&::before, &::after": { border: "none" },
                }}
            />

            <Box
                sx={{
                    flex: 1,
                    gap: 2,
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                }}
            >
                <Typography variant="textLarge" color="common.white">
                    {title}
                </Typography>

                {loading ? (
                    <SummarySkeletonGrid
                        fields={fields}
                    />
                ) : empty ? (
                    <EmptySummaryItem text={emptyText} />
                ) : (
                    <Grid container spacing={2}>
                        {fields.map((field) => (
                            <Grid
                                key={field.label}
                                item
                                {...(field.gridProps ?? fieldGridProps)}
                            >
                                <SummaryItem
                                    label={field.label}
                                    value={field.value}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Box
                    sx={{
                        maxWidth: "344px",
                        opacity: loading ? 0.5 : 1,
                        pointerEvents: loading ? "none" : "auto",
                    }}
                >
                    <ButtonWithIcon
                        onClick={canClick ? onClick : undefined}
                        icon={<CheckIcon />}
                        disabled={loading}
                    >
                        MOSTRAR TODOS
                    </ButtonWithIcon>
                </Box>
            </Box>
        </Box>
    )
}

SummarySection.propTypes = {
    title: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
                PropTypes.node,
            ]),
            gridProps: PropTypes.object,
        })
    ),
    empty: PropTypes.bool,
    emptyText: PropTypes.string,
    onClick: PropTypes.func,
    fieldGridProps: PropTypes.object,
    loading: PropTypes.bool,
}

SummarySection.defaultProps = {
    fields: [],
    empty: false,
    emptyText: "",
    onClick: null,
    fieldGridProps: {
        xxxs: 4,
        sm: "auto",
    },
    loading: false,
}

export default SummarySection